import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { api } from "../lib/api";

export default function Svara() {
    const { token } = useParams();
    const [state, setState] = useState({ status: "loading", data: null });
    const [answers, setAnswers] = useState({});
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/public/link/${token}`)
            .then((r) => setState({ status: "ok", data: r.data }))
            .catch(() => setState({ status: "expired", data: null }));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        const svar = (state.data?.fragor || []).map((f) => ({ fraga: f, svar: answers[f] || "" })).filter((s) => s.svar.trim());
        if (!svar.length) {
            setError("Svara på minst en fråga innan du skickar.");
            return;
        }
        setSending(true);
        try {
            const r = await api.post(`/public/link/${token}/respond`, { svar });
            setDone(r.data);
        } catch {
            setError("Kunde inte skicka svaren – länken kan ha gått ut.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen canvas-bg flex items-center justify-center px-4 py-10" data-testid="svara-page">
            <div className="w-full max-w-xl">
                <div className="glass-panel rounded-2xl p-8 shadow-2xl">
                    <div className="flex items-center gap-3 mb-6">
                        <img src="/hamn-emblem.png" alt="HAMN" data-testid="svara-logo"
                            className="w-10 h-10 rounded-full border border-current/40 object-cover" />
                        <div>
                            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">Anonym enkät</p>
                            <h1 className="font-display font-extrabold text-xl tracking-tight text-slate-100">
                                {state.data?.title || "Utbildning"}
                            </h1>
                        </div>
                    </div>

                    {state.status === "loading" && (
                        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Laddar …</div>
                    )}

                    {state.status === "expired" && (
                        <p data-testid="svara-expired" className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2.5">
                            Den här länken har gått ut eller stängts. Kontakta din utbildare för en ny länk.
                        </p>
                    )}

                    {state.status === "ok" && !done && (
                        <form onSubmit={submit} className="space-y-5">
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Dina svar är anonyma – inget namn, ingen e-post och ingen teknisk spårning kopplas till dig.
                                Svaren sammanställs på gruppnivå av utbildaren.
                            </p>
                            {state.data.fragor.map((f, i) => (
                                <div key={i}>
                                    <label className="text-sm font-semibold text-slate-200 block mb-2">{i + 1}. {f}</label>
                                    <textarea
                                        data-testid={`svara-answer-${i}`}
                                        rows={3}
                                        value={answers[f] || ""}
                                        onChange={(e) => setAnswers({ ...answers, [f]: e.target.value })}
                                        className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-current/60 transition-colors"
                                        placeholder="Ditt svar …"
                                    />
                                </div>
                            ))}
                            {error && <p data-testid="svara-error" className="text-sm text-red-400">{error}</p>}
                            <button data-testid="svara-submit-button" type="submit" disabled={sending}
                                className="w-full flex items-center justify-center gap-2 bg-current text-abyss font-semibold text-sm rounded-lg py-3 hover:bg-tide transition-colors disabled:opacity-50">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                Skicka svar anonymt
                            </button>
                        </form>
                    )}

                    {done && (
                        <div data-testid="svara-thanks" className="text-center py-6">
                            <CheckCircle2 className="w-10 h-10 text-buoy mx-auto mb-3" />
                            <h2 className="font-display font-bold text-lg text-slate-100">Tack för dina svar!</h2>
                            <p className="text-sm text-slate-400 mt-1">
                                Dina svar har registrerats anonymt som <span className="font-mono text-buoy">{done.pseudonym}</span>.
                            </p>
                        </div>
                    )}
                </div>
                <p className="text-center font-mono text-[10px] uppercase tracking-widest text-slate-600 mt-6">
                    HAMN ATLAS · anonym datainsamling
                </p>
            </div>
        </div>
    );
}
